import React, { useState } from 'react';
import { Card, Form, Button, Row, Col, Alert, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faStar, faStar as faStarEmpty } from '@fortawesome/free-solid-svg-icons';
import '../common.css';

// Social media platform options
const socialPlatforms = [
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "twitter", label: "Twitter" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "tiktok", label: "TikTok" },
  { id: "youtube", label: "YouTube" },
  { id: "snapchat", label: "Snapchat" },
  { id: "pinterest", label: "Pinterest" },
  { id: "reddit", label: "Reddit" },
  { id: "other", label: "Other" }
];

// Marketing channels options
const marketingChannels = [
  { id: "email", label: "Email marketing" },
  { id: "social", label: "Social media platforms" },
  { id: "print", label: "Print media" },
  { id: "radio", label: "Radio/broadcast" },
  { id: "digital", label: "Digital advertising" },
  { id: "influencer", label: "Influencer partnerships" },
  { id: "industry", label: "Industry publications" },
  { id: "community", label: "Community outreach" },
  { id: "direct", label: "Direct mail" },
  { id: "pr", label: "PR/press releases" },
  { id: "other", label: "Other" }
];

// Promotional assets options
const promotionalAssets = [
  { id: "photos", label: "Professional photos" },
  { id: "video", label: "Video content" },
  { id: "press", label: "Press kit" },
  { id: "social", label: "Social media accounts" },
  { id: "email", label: "Email list" },
  { id: "website", label: "Website/landing page" },
  { id: "guidelines", label: "Existing brand guidelines" },
  { id: "materials", label: "Previous promotional materials" },
  { id: "testimonials", label: "Testimonials/reviews" },
  { id: "other", label: "Other" }
];

const MarketingPromotion = ({ formData, onInputChange, onNext, onPrev, error }) => {
  const [channelRatings, setChannelRatings] = useState(formData.channelRatings || {});
  
  // Handle checkbox changes for multi-select options
  const handleMultiSelectChange = (field, value, isChecked) => {
    const currentValues = formData[field] || [];
    let newValues;
    
    if (isChecked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(item => item !== value);
    }
    
    onInputChange({ [field]: newValues });
  };

  // Handle input changes for text fields
  const handleInputChange = (field, value) => {
    onInputChange({ [field]: value });
  };

  // Check if a value is selected in a multi-select list
  const isSelected = (field, value) => {
    return (formData[field] || []).includes(value);
  };
  
  // Handle importance rating for marketing channels
  const handleRating = (channelId, rating) => {
    const newRatings = { ...channelRatings, [channelId]: rating };
    setChannelRatings(newRatings);
    onInputChange({ channelRatings: newRatings });
  };
  
  // Get rating for a channel
  const getRating = (channelId) => {
    return channelRatings[channelId] || 0;
  };
  
  // Render star rating component
  const renderStarRating = (channelId) => {
    const maxRating = 5;
    const stars = [];
    
    for (let i = 1; i <= maxRating; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={i <= getRating(channelId) ? faStar : faStarEmpty}
          className="me-1"
          style={{ 
            cursor: 'pointer', 
            color: i <= getRating(channelId) ? '#ffb400' : '#6c757d'
          }}
          onClick={() => handleRating(channelId, i)}
        />
      );
    }
    
    return <div className="ms-2">{stars}</div>;
  };

  return (
    <Card className="dark-main-card text-white mb-4">
      <Card.Header className="dark-card-header border-bottom">
        <h4 className="mb-0 text-white">Define Your Promotional Strategy</h4>
      </Card.Header>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="dark-alert mb-4">{error}</Alert>}
        
        <Form>
          {/* Advance Marketing Timeline */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Advance Marketing Timeline</h5>
            
            <Row>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Marketing start date</Form.Label>
                  <Form.Control
                    type="date"
                    className="dark-input"
                    value={formData.marketingStartDate || ''}
                    onChange={(e) => handleInputChange('marketingStartDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Announcement date</Form.Label>
                  <Form.Control
                    type="date"
                    className="dark-input"
                    value={formData.announcementDate || ''}
                    onChange={(e) => handleInputChange('announcementDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Ticket/registration launch date</Form.Label>
                  <Form.Control
                    type="date"
                    className="dark-input"
                    value={formData.ticketLaunchDate || ''}
                    onChange={(e) => handleInputChange('ticketLaunchDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
          
          {/* Target Marketing Channels */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Target Marketing Channels</h5>
            <p className="text-light small mb-3">Select the channels you'll use and rate their importance (5 = highest)</p>
            
            {marketingChannels.slice(0, marketingChannels.length - 1).map((channel) => (
              <Form.Group key={channel.id} className="mb-2">
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id={`channel-${channel.id}`}
                    className="dark-checkbox"
                    label={channel.label}
                    checked={isSelected('marketingChannels', channel.id)}
                    onChange={(e) => handleMultiSelectChange('marketingChannels', channel.id, e.target.checked)}
                  />
                  {isSelected('marketingChannels', channel.id) && renderStarRating(channel.id)}
                </div>
                
                {/* Show social platforms if social media is selected */}
                {channel.id === 'social' && isSelected('marketingChannels', 'social') && (
                  <div className="ms-4 mt-2">
                    <Form.Label className="text-white">Select platforms:</Form.Label>
                    <Row>
                      {socialPlatforms.slice(0, socialPlatforms.length - 1).map((platform) => (
                        <Col md={4} key={platform.id} className="mb-2">
                          <Form.Check
                            type="checkbox"
                            id={`platform-${platform.id}`}
                            className="dark-checkbox"
                            label={platform.label}
                            checked={isSelected('socialPlatforms', platform.id)}
                            onChange={(e) => handleMultiSelectChange('socialPlatforms', platform.id, e.target.checked)}
                          />
                        </Col>
                      ))}
                      <Col md={4} className="mb-2">
                        <Form.Check
                          type="checkbox"
                          id="platform-other"
                          className="dark-checkbox"
                          label="Other"
                          checked={isSelected('socialPlatforms', 'other')}
                          onChange={(e) => handleMultiSelectChange('socialPlatforms', 'other', e.target.checked)}
                        />
                      </Col>
                      {isSelected('socialPlatforms', 'other') && (
                        <Col md={8} className="mb-2">
                          <Form.Control
                            type="text"
                            className="dark-input"
                            placeholder="Specify other platform"
                            value={formData.socialPlatformsOther || ''}
                            onChange={(e) => handleInputChange('socialPlatformsOther', e.target.value)}
                          />
                        </Col>
                      )}
                    </Row>
                  </div>
                )}
              </Form.Group>
            ))}
            
            <Form.Group className="mb-2">
              <div className="d-flex align-items-center">
                <Form.Check
                  type="checkbox"
                  id="channel-other"
                  className="dark-checkbox"
                  label="Other"
                  checked={isSelected('marketingChannels', 'other')}
                  onChange={(e) => handleMultiSelectChange('marketingChannels', 'other', e.target.checked)}
                />
                {isSelected('marketingChannels', 'other') && renderStarRating('other')}
              </div>
              {isSelected('marketingChannels', 'other') && (
                <Form.Control
                  type="text"
                  className="dark-input mt-2"
                  style={{ width: '50%', marginLeft: '1.5rem' }}
                  placeholder="Specify other marketing channel"
                  value={formData.marketingChannelsOther || ''}
                  onChange={(e) => handleInputChange('marketingChannelsOther', e.target.value)}
                />
              )}
            </Form.Group>
          </div>
          
          {/* Promotional Assets Available */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Promotional Assets Available</h5>
            
            <Row>
              {promotionalAssets.map((asset) => (
                <Col md={6} key={asset.id} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id={`asset-${asset.id}`}
                    className="dark-checkbox"
                    label={asset.label}
                    checked={isSelected('promotionalAssets', asset.id)}
                    onChange={(e) => handleMultiSelectChange('promotionalAssets', asset.id, e.target.checked)}
                  />
                  {asset.id === 'social' && isSelected('promotionalAssets', 'social') && (
                    <InputGroup className="mt-2 mb-3" style={{ width: '90%', marginLeft: '1.5rem' }}>
                      <Form.Control
                        type="number"
                        className="dark-input"
                        placeholder="Number of followers"
                        min="0"
                        value={formData.socialFollowers || ''}
                        onChange={(e) => handleInputChange('socialFollowers', e.target.value)}
                      />
                      <InputGroup.Text className="dark-input-group-text">followers</InputGroup.Text>
                    </InputGroup>
                  )}
                  {asset.id === 'email' && isSelected('promotionalAssets', 'email') && (
                    <InputGroup className="mt-2 mb-3" style={{ width: '90%', marginLeft: '1.5rem' }}>
                      <Form.Control
                        type="number"
                        className="dark-input"
                        placeholder="Number of subscribers"
                        min="0"
                        value={formData.emailSubscribers || ''}
                        onChange={(e) => handleInputChange('emailSubscribers', e.target.value)}
                      />
                      <InputGroup.Text className="dark-input-group-text">subscribers</InputGroup.Text>
                    </InputGroup>
                  )}
                  {asset.id === 'other' && isSelected('promotionalAssets', 'other') && (
                    <Form.Control
                      type="text"
                      className="dark-input mt-2 mb-3"
                      style={{ width: '90%', marginLeft: '1.5rem' }}
                      placeholder="Specify other asset"
                      value={formData.promotionalAssetsOther || ''}
                      onChange={(e) => handleInputChange('promotionalAssetsOther', e.target.value)}
                    />
                  )}
                </Col>
              ))}
            </Row>
          </div>
          
          {/* Local Marketing Needs */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Local Marketing Needs</h5>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="local-partnerships"
                className="dark-checkbox"
                label="Local partnerships desired"
                checked={formData.localPartnerships || false}
                onChange={(e) => handleInputChange('localPartnerships', e.target.checked)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="media-appearances"
                className="dark-checkbox"
                label="Media appearances/interviews"
                checked={formData.mediaAppearances || false}
                onChange={(e) => handleInputChange('mediaAppearances', e.target.checked)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Local advertising budget per location</Form.Label>
              <InputGroup>
                <InputGroup.Text className="dark-input-group-text">$</InputGroup.Text>
                <Form.Control
                  type="text"
                  className="dark-input"
                  placeholder="Budget amount"
                  value={formData.localAdBudget || ''}
                  onChange={(e) => handleInputChange('localAdBudget', e.target.value.replace(/[^\d.]/g, ''))}
                />
              </InputGroup>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="promo-events"
                className="dark-checkbox"
                label="Promotional events before main event"
                checked={formData.promoEvents || false}
                onChange={(e) => handleInputChange('promoEvents', e.target.checked)}
              />
              
              {formData.promoEvents && (
                <Form.Control
                  type="text"
                  className="dark-input mt-2"
                  style={{ width: '90%', marginLeft: '1.5rem' }}
                  placeholder="Describe promotional events"
                  value={formData.promoEventsDetails || ''}
                  onChange={(e) => handleInputChange('promoEventsDetails', e.target.value)}
                />
              )}
            </Form.Group>
          </div>
          
          {/* Additional Marketing Notes */}
          <Form.Group className="mb-4">
            <Form.Label className="text-white">Additional Marketing Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="dark-input"
              placeholder="Any other marketing or promotional considerations"
              value={formData.additionalMarketingNotes || ''}
              onChange={(e) => handleInputChange('additionalMarketingNotes', e.target.value)}
            />
          </Form.Group>
          
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-light"
              className="dark-outline-button"
              onClick={onPrev}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
              Back
            </Button>
            <Button
              className="dark-primary-button"
              onClick={onNext}
            >
              Continue
              <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default MarketingPromotion;